/**
 * Flow API Service
 * Gestisce salvataggio e caricamento flussi da PostgreSQL (Neon)
 */

import { API_URL } from '../config';

export interface FlowData {
  id?: string;
  version?: string;
  metadata?: {
    created?: string;
    engine?: string;
    format?: string;
  };
  nodes: any[];
  edges: any[];
  timestamp?: string;
}

export interface SavedFlow {
  id: string;
  user_id?: string;
  name: string;
  flow_data: FlowData;
  version: string;
  description?: string;
  tags?: string[];
  is_shared: boolean;
  is_template?: boolean;  // True se è un template pubblico (creato da admin)
  created_at: string;
  updated_at: string;
}

export interface FlowCreate {
  name: string;
  flow_data: FlowData;
  version?: string;
  description?: string;
  tags?: string[];
  is_shared?: boolean;
  is_template?: boolean;  // Solo admin può settare a true
}

export interface FlowUpdate {
  name?: string;
  flow_data?: FlowData;
  version?: string;
  description?: string;
  tags?: string[];
  is_shared?: boolean;
  is_template?: boolean;  // Solo admin può settare a true
}

/**
 * Crea o aggiorna un flow (se esiste già con stesso nome, lo aggiorna)
 * @param flow Dati del flow
 * @param userId Auth0 user ID
 * @param userEmail Email utente (per verificare se admin)
 */
export async function createFlow(
  flow: FlowCreate, 
  userId: string, 
  userEmail?: string
): Promise<SavedFlow> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-User-ID': userId,
  };
  
  // Passa email per verificare admin lato backend
  if (userEmail) {
    headers['X-User-Email'] = userEmail;
  }
  
  const response = await fetch(`${API_URL}/api/prompts/flows`, {
    method: 'POST',
    headers,
    body: JSON.stringify(flow),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Errore sconosciuto' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Recupera flow per ID
 */
export async function getFlow(flowId: string, userId: string): Promise<SavedFlow> {
  const response = await fetch(`${API_URL}/api/prompts/flows/${flowId}`, {
    method: 'GET',
    headers: {
      'X-User-ID': userId,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Flow non trovato' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Recupera flow per nome
 */
export async function getFlowByName(flowName: string, userId: string): Promise<SavedFlow> {
  const response = await fetch(`${API_URL}/api/prompts/flows/by-name/${encodeURIComponent(flowName)}`, {
    method: 'GET',
    headers: {
      'X-User-ID': userId,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Flow non trovato' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Lista tutti i flows dell'utente (o di tutti se admin)
 */
export async function listFlows(
  userId: string,
  options?: {
    search?: string;
    limit?: number;
    offset?: number;
    allUsers?: boolean;  // Se true e admin, mostra tutti i flow
    userEmail?: string;  // Email per verifica admin
  }
): Promise<{ flows: SavedFlow[]; total: number; is_admin_view?: boolean }> {
  const params = new URLSearchParams();
  if (options?.search) params.append('search', options.search);
  if (options?.limit) params.append('limit', options.limit.toString());
  if (options?.offset) params.append('offset', options.offset.toString());
  if (options?.allUsers) params.append('all_users', 'true');

  const headers: Record<string, string> = {
    'X-User-ID': userId,
  };
  
  // Passa email per verifica admin lato server
  if (options?.userEmail) {
    headers['X-User-Email'] = options.userEmail;
  }

  const response = await fetch(`${API_URL}/api/prompts/flows?${params.toString()}`, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Errore lista flows' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Aggiorna flow esistente
 */
export async function updateFlow(
  flowId: string,
  userId: string,
  flow: FlowUpdate,
  userEmail?: string
): Promise<SavedFlow> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-User-ID': userId,
  };
  
  // Passa email per verificare admin lato backend
  if (userEmail) {
    headers['X-User-Email'] = userEmail;
  }
  
  const response = await fetch(`${API_URL}/api/prompts/flows/${flowId}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(flow),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Errore aggiornamento flow' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Elimina flow
 */
export async function deleteFlow(flowId: string, userId: string): Promise<void> {
  const response = await fetch(`${API_URL}/api/prompts/flows/${flowId}`, {
    method: 'DELETE',
    headers: {
      'X-User-ID': userId,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Errore eliminazione flow' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }
}

/**
 * Elimina flow per nome
 */
export async function deleteFlowByName(flowName: string, userId: string): Promise<void> {
  const response = await fetch(`${API_URL}/api/prompts/flows/by-name/${encodeURIComponent(flowName)}`, {
    method: 'DELETE',
    headers: {
      'X-User-ID': userId,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Errore eliminazione flow' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }
}

